import { BodyText, DetailText, Link } from '@utilitywarehouse/hearth-react';

import { DISCOUNT_IDS } from '@constants/apiConstants';
import { EXTERNAL_LINKS } from '@constants/externalLinks';
import { ChargeAmount, Discount } from '@generated/gql/graphql';
import { formatCurrency } from '@utils/format';
import { isAmountCredit } from '@utils/price';

import './styles.scss';

const BHC_DISCOUNT_DURATION_MONTHS = 3;
const MONTHS_IN_YEAR = 12;
const BHCPriceHeader = ({
  monthlyCharge,
  discounts,
}: {
  monthlyCharge: ChargeAmount | undefined;
  discounts?: Discount[];
}) => {
  const bhcDiscount = discounts?.find(
    (d) =>
      d.qualified &&
      d.id === DISCOUNT_IDS.INSURANCE_BOILER_THREE_MONTHS_FREE_DISCOUNT &&
      isAmountCredit(d.credit)
  );

  const fullPrice = parseFloat(monthlyCharge?.value ?? '0');

  if (!bhcDiscount || !isAmountCredit(bhcDiscount.credit)) {
    return (
      <DetailText size="3xl" data-testid="bhc-hero-price">
        {formatCurrency({ value: fullPrice })}{' '}
        <BodyText as="span">a month</BodyText>
      </DetailText>
    );
  }

  const discountDurationMonths = bhcDiscount.creditFrequency
    ? bhcDiscount.creditFrequency
    : BHC_DISCOUNT_DURATION_MONTHS; // fallback to constant if null/empty
  const discountedPrice = fullPrice - parseFloat(bhcDiscount.credit.amount);
  const remainingMonths = MONTHS_IN_YEAR - discountDurationMonths;
  const originalAnnual = fullPrice * MONTHS_IN_YEAR;
  const discountedAnnual =
    discountedPrice * discountDurationMonths + fullPrice * remainingMonths;

  return (
    <div className="bhc-price-header">
      <DetailText size="3xl" data-testid="bhc-hero-price">
        {formatCurrency({ value: discountedPrice })}
      </DetailText>
      <div className="bhc-price-header__text">
        <BodyText as="p">
          for {discountDurationMonths} months, then{' '}
          <BodyText as="span" weight="bold">
            {formatCurrency({ value: fullPrice })}
          </BodyText>{' '}
          for {remainingMonths} months.
        </BodyText>
        <BodyText size="sm">
          This is an annual policy (
          <s>{formatCurrency({ value: originalAnnual })}</s>{' '}
          {formatCurrency({ value: discountedAnnual })} a year).{' '}
          <Link
            href={EXTERNAL_LINKS.bhcPromoTerms}
            target="_blank"
            rel="noopener noreferrer"
          >
            <strong>
              <u>Terms apply.</u>
            </strong>
          </Link>
        </BodyText>
      </div>
    </div>
  );
};

export default BHCPriceHeader;
